/**
 * Currency formatting utilities for RifaMaker.
 *
 * BRL (Real): R$ 1.250,00 (dot for thousands, comma for decimals)
 * USD (Dollar): $1,250.00 (comma for thousands, dot for decimals)
 */

/**
 * Format a numeric value according to the currency.
 * @param {number} value - The numeric value to format
 * @param {string} currency - 'BRL' or 'USD'
 * @param {boolean} showSymbol - Whether to prepend the currency symbol (default: true)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, currency = 'BRL', showSymbol = true) {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);

  if (currency === 'BRL') {
    const formatted = num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return showSymbol ? `R$ ${formatted}` : formatted;
  }

  // USD
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return showSymbol ? `$ ${formatted}` : formatted;
}

/**
 * Get currency symbol.
 */
export function getCurrencySymbol(currency) {
  return currency === 'BRL' ? 'R$' : '$';
}


/**
 * PIX Key formatting and validation utilities.
 */

/**
 * Format a CPF string: 000.000.000-00
 */
export function formatCPF(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Format a CNPJ string: 00.000.000/0000-00
 */
export function formatCNPJ(value) {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/**
 * Format a phone number: (XX) XXXXX-XXXX
 * Accepts with or without country code. Strips +55 if provided.
 */
export function formatPhone(value) {
  let digits = value.replace(/\D/g, '');

  // Strip leading 55 if the user typed the country code (13 digits = +55 + 11 digits)
  if (digits.length > 11 && digits.startsWith('55')) {
    digits = digits.slice(2);
  }

  digits = digits.slice(0, 11);

  if (digits.length === 0) return '';

  // Format as (XX) XXXXX-XXXX
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Validate a PIX key based on its type.
 * Returns true if valid, error message key if invalid.
 * Accepts any format — only checks that the field is not empty.
 */
export function validatePixKey(value, keyType) {
  if (!value || !value.trim()) return 'createRaffle.pixRequired';

  // For email type, do a minimal check
  if (keyType === 'email') {
    if (!value.includes('@') || !value.split('@')[1]?.includes('.')) {
      return 'createRaffle.pixInvalidEmail';
    }
  }

  return true;
}

/**
 * Auto-format a PIX key value based on its type.
 */
export function formatPixKey(value, keyType) {
  switch (keyType) {
    case 'cpf':
      return formatCPF(value);
    case 'cnpj':
      return formatCNPJ(value);
    case 'phone':
      return formatPhone(value);
    default:
      return value; // email and random: no formatting
  }
}
